import type { RaclerMessage } from "@racler/protocol";
import type { RelayClient } from "../relayClient.ts";
import type { LspStartOutcome } from "./lspClient.ts";
import type { LspMessageLike, LspTransport } from "./types.ts";

const TS_FAMILY = "typescript";

/**
 * Starts a FRESH Agent-side LSP session over the authenticated relay socket. The browser names only a Project and a
 * language family; the Agent picks executable, argv and root. The returned transport carries standard LSP JSON-RPC.
 */
export async function startRelaySession(
  relay: RelayClient,
  machineId: string,
  projectId: string,
  languageId: string = TS_FAMILY,
): Promise<LspStartOutcome> {
  const sessionId = crypto.randomUUID();
  const messageListeners = new Set<(m: LspMessageLike) => void>();
  const closeListeners = new Set<(reason: string) => void>();
  let closed = false;
  const finish = (reason: string) => {
    if (closed) return;
    closed = true;
    offEvents();
    offState();
    for (const l of closeListeners) l(reason);
  };

  const offEvents = relay.on((msg: RaclerMessage) => {
    if (msg.channel !== "lsp" || msg.machineId !== machineId) return;
    const p = msg.payload as { sessionId?: string; message?: LspMessageLike; state?: string };
    if (p.sessionId !== sessionId) return;
    if (msg.type === "lsp.message" && p.message) {
      for (const l of messageListeners) l(p.message);
    } else if (msg.type === "lsp.status") {
      const detail = (msg.payload as { message?: string }).message;
      finish(p.state === "crashed" ? (detail ?? "language server crashed") : (detail ?? "stopped"));
    }
  });
  // A socket reconnect drops our Agent session (sessions are not durable): the client starts a fresh one.
  const offState = relay.onState((s) => {
    if (s === "closed") finish("connection lost");
  });

  const transport: LspTransport = {
    send: (message) => {
      if (closed) return;
      relay.notify(machineId, "lsp/lsp.message", { sessionId, message: { ...message } });
    },
    onMessage: (fn) => {
      messageListeners.add(fn);
      return () => messageListeners.delete(fn);
    },
    onClose: (fn) => {
      closeListeners.add(fn);
      return () => closeListeners.delete(fn);
    },
    close: () => {
      if (closed) return;
      relay.notify(machineId, "lsp/lsp.stop", { sessionId });
      finish("stopped");
    },
  };

  try {
    const r = await relay.machine(machineId, "lsp/lsp.start", { sessionId, projectId, languageId });
    if (r.state !== "ready") {
      finish("unavailable");
      return { transport, state: "unavailable", ...(r.reason ? { reason: r.reason } : {}) };
    }
    return { transport, state: "ready" };
  } catch (err) {
    // The start may have been executed even though the answer was lost: make sure no process stays behind.
    relay.notify(machineId, "lsp/lsp.stop", { sessionId });
    finish("start failed");
    throw err;
  }
}
